import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * `@vaarta/suite-launcher` — the app grid, once, for every application in the suite.
 *
 * Read `core.ts` first: it carries the measurement this package answers (the launcher
 * existed four times), why this is not part of `@vaarta/auth-flow`, and the two properties
 * that are tested rather than promised — no application is named in this source, and no
 * entitlement vocabulary appears in it.
 *
 * ── WHY IT CARRIES ITS OWN STYLES, WHICH LOOKS WRONG AND IS NOT ────────────────────────
 * The four implementations each render their host's design system: Analytics uses its
 * `Menu`/`MenuItem`/`IconButton`, Automation Studio and Data Studio their own `Menu`,
 * Converse its sidebar primitives. There are four such systems and they are not the same
 * components, so a shared panel cannot import any of them — and adding a fifth dependency
 * that all four must install is a worse trade than owning the pixels.
 *
 * So: inline styles for layout and colour, plus ONE injected stylesheet for the three
 * things inline styles cannot express — `:focus-visible`, `:hover`, and the phone-width
 * `@media` rule. ⛔ NOT a `.css` file: `sideEffects: false` in package.json would let a
 * bundler drop a side-effect-only import, and four bundler versions (Vite 5 in Data Studio,
 * 6 in Converse, 7 in Automation Studio, 8 in Analytics) disagree about how a package's CSS
 * is resolved at all. Converse's own widget launcher reached the same conclusion for the
 * same reason and says so: it is inline-styled because it is the one surface that must
 * still render when what is inside it fails.
 *
 * Every colour is a CSS custom property on the root, so a host that wants to theme the
 * panel overrides `--vsl-*` in its own stylesheet and nothing here needs to know.
 *
 * ── WHY THE GRID ICON IS ALWAYS THERE, WHICH IS A CHANGE ───────────────────────────────
 * ⚠️ All four measured implementations hide the control unless the person already holds a
 * sibling, and fetch the list on MOUNT to find out — so every page view of every product
 * made a cross-origin request to Identity Studio in order to decide whether to draw one
 * small button. This mounts the button whenever it is configured and fetches on FIRST OPEN
 * instead. Three consequences, all of them the point:
 *
 * 1. It is discoverable. A control that appears only once you already have somewhere to go
 *    cannot teach anybody that the suite exists — and teaching that is the ask.
 * 2. One request per person who asks, instead of one per page view per product.
 * 3. The empty and failed states become visible, which is what makes them worth writing
 *    honestly. Previously a 502 from Identity Studio — which happens for about a minute on
 *    every deploy — and "you hold only this application" were the same silent blank.
 *
 * ⚠️ It stays the HOST's decision whether to mount this at all, and one host has a real
 * reason not to: Converse can run as an embedded widget on a third party's site, where the
 * suite cookie belongs to whoever is signed in on that machine and not to the host page's
 * user. Converse therefore gates the mount on its own `items.suiteLauncher`, and that gate
 * is preserved rather than moved in here — this package cannot know it is embedded.
 */
import { useCallback, useEffect, useId, useRef, useState, } from 'react';
import { FILTER_THRESHOLD, fetchSuiteTiles, filterTiles, groupTiles, monogram, PANEL_DESIRED_HEIGHT, preferredDrop, shouldShowFilter, tileAccent, tileAccessibleName, tileKind, } from './core';
// AG-2415 — the tenancy switcher lives beside the launcher, in the same package, so every application mounts both
// from the one vendored copy. Its pure half is `tenancy.ts`, its markup `tenancy-switcher.tsx`.
export { TenancySwitcher, TenancySwitcherBody, } from './tenancy-switcher';
export { LIST_FAILED_MESSAGE, NOT_YOURS_MESSAGE, SESSION_ENDED_MESSAGE, SWITCH_FAILED_MESSAGE, currentTenancy, readSwitchOutcome, readTenancies, tenancySwitcherIsWorthShowing, } from './tenancy';
export { FILTER_THRESHOLD, UNCATEGORISED_LABEL, fetchSuiteTiles, filterTiles, groupTiles, launcherErrorMessage, launcherIsWorthShowing, monogram, readSuiteTiles, safeLaunchUrl, PANEL_DESIRED_HEIGHT, preferredDrop, shouldShowFilter, tileAccent, tileAccessibleName, tileKind, UNREACHABLE_MESSAGE, } from './core';
/** Marks a focusable tile for the arrow-key walk. Queried from the DOM, not tracked in state. */
const TILE_ATTR = 'data-vsl-tile';
export function SuiteLauncher({ launcherUrl, currentAppName, filterThreshold = FILTER_THRESHOLD, align = 'right', fetchImpl, triggerClassName, triggerLabel = 'Vaarta applications', }) {
    const [open, setOpen] = useState(false);
    /** Which way the panel opens. Decided from the trigger's position when it is opened. */
    const [drop, setDrop] = useState('down');
    const [state, setState] = useState({ status: 'idle' });
    const [query, setQuery] = useState('');
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const filterRef = useRef(null);
    const panelId = useId();
    useStyleSheet();
    /**
     * Ask, and keep the answer. Re-asking on every open would make the panel flicker for a
     * person switching back and forth; `reload` exists for the error state's Retry, which is
     * the one case where asking again is the whole point.
     */
    const load = useCallback((force) => {
        if (!launcherUrl)
            return;
        setState((prev) => {
            if (!force && (prev.status === 'loading' || prev.status === 'ready'))
                return prev;
            return { status: 'loading' };
        });
        const controller = new AbortController();
        void (async () => {
            const next = await fetchSuiteTiles({ url: launcherUrl, fetchImpl, signal: controller.signal });
            if (!controller.signal.aborted)
                setState(next);
        })();
        return () => controller.abort();
    }, [launcherUrl, fetchImpl]);
    useEffect(() => {
        if (open)
            load(false);
    }, [open, load]);
    // A changed URL invalidates the answer — it means the auth config arrived or was replaced.
    useEffect(() => {
        setState({ status: 'idle' });
    }, [launcherUrl]);
    const close = useCallback((refocus) => {
        setOpen(false);
        setQuery('');
        if (refocus)
            triggerRef.current?.focus();
    }, []);
    /** Pointer outside the root closes it. `pointerdown`, so it happens before a focus move. */
    useEffect(() => {
        if (!open)
            return;
        const onPointerDown = (event) => {
            const target = event.target;
            if (target && rootRef.current && !rootRef.current.contains(target))
                close(false);
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        return () => document.removeEventListener('pointerdown', onPointerDown, true);
    }, [open, close]);
    // ⚠️ The threshold is measured against EVERY tile the person holds, not against the
    // filtered subset — otherwise typing a query that narrows the list to six would remove
    // the box you are typing into.
    const showFilter = shouldShowFilter(state.status === 'ready' ? state.tiles : [], filterThreshold);
    /**
     * Move focus in when the panel opens — to the filter box when there is one, because the
     * ask is "type-to-filter", and typing into a box you must first Tab to is not that.
     * Otherwise the first tile, so the arrow walk has somewhere to start.
     */
    useEffect(() => {
        if (!open || state.status !== 'ready')
            return;
        const id = requestAnimationFrame(() => {
            if (filterRef.current) {
                filterRef.current.focus();
                return;
            }
            firstTile(panelRef.current)?.focus();
        });
        return () => cancelAnimationFrame(id);
    }, [open, state.status]);
    /**
     * Escape closes; arrows walk the tiles; Home/End jump.
     *
     * ⚠️ The arrows walk LINEARLY — Right and Down both mean "next" — rather than in true
     * two-dimensional grid order. A real 2-D walk needs the rendered column count, which here
     * is set by a `repeat(auto-fill, minmax(...))` grid and therefore by the panel's measured
     * width; reading it back per keystroke to move focus is a lot of machinery for a control
     * with at most a handful of rows, and it goes wrong at exactly the widths nobody tests.
     * Linear is predictable, wraps, and never lands on nothing.
     */
    const onPanelKeyDown = useCallback((event) => {
        if (event.key === 'Escape') {
            event.stopPropagation();
            close(true);
            return;
        }
        const step = event.key === 'ArrowRight' || event.key === 'ArrowDown'
            ? 1
            : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
                ? -1
                : 0;
        const items = allTiles(panelRef.current);
        if (!items.length)
            return;
        if (event.key === 'Home' || event.key === 'End') {
            event.preventDefault();
            (event.key === 'Home' ? items[0] : items[items.length - 1])?.focus();
            return;
        }
        if (step === 0)
            return;
        // From the filter box, an arrow means "into the grid" rather than "past it".
        const active = document.activeElement;
        const at = active ? items.indexOf(active) : -1;
        if (at === -1) {
            event.preventDefault();
            (step === 1 ? items[0] : items[items.length - 1])?.focus();
            return;
        }
        event.preventDefault();
        items[(at + step + items.length) % items.length]?.focus();
    }, [close]);
    if (!launcherUrl)
        return null;
    const rootStyle = { position: 'relative', display: 'inline-flex' };
    return (_jsxs("div", { ref: rootRef, className: "vsl-root", style: rootStyle, children: [_jsx("button", { ref: triggerRef, type: "button", className: `vsl-trigger${triggerClassName ? ` ${triggerClassName}` : ''}`, "aria-label": triggerLabel, "aria-haspopup": "dialog", "aria-expanded": open, "aria-controls": open ? panelId : undefined, onClick: () => {
                    if (open) {
                        close(true);
                        return;
                    }
                    // ⚠️ Measured BEFORE the panel exists, so it is placed correctly on its first
                    // paint rather than being corrected afterwards. Converse mounts this in the
                    // sidebar footer, where opening downward puts the panel below the fold.
                    const rect = triggerRef.current?.getBoundingClientRect();
                    const viewport = typeof window === 'undefined' ? 0 : window.innerHeight;
                    setDrop(rect
                        ? preferredDrop({
                            spaceAbove: rect.top,
                            spaceBelow: viewport - rect.bottom,
                            desired: PANEL_DESIRED_HEIGHT,
                        })
                        : 'down');
                    setOpen(true);
                }, children: _jsx(WaffleIcon, {}) }), open && (_jsxs("div", { ref: panelRef, id: panelId, role: "dialog", "aria-modal": "false", "aria-label": triggerLabel, className: `vsl-panel vsl-panel--${align} vsl-panel--${drop}`, onKeyDown: onPanelKeyDown, children: [_jsx("div", { className: "vsl-panel-head", children: _jsx("span", { className: "vsl-panel-title", children: triggerLabel }) }), showFilter && (_jsx("input", { ref: filterRef, type: "search", className: "vsl-filter", value: query, placeholder: "Filter applications", "aria-label": "Filter applications", autoComplete: "off", onChange: (event) => setQuery(event.target.value) })), _jsx("div", { className: "vsl-body", children: _jsx(SuiteLauncherBody, { state: state, query: query, currentAppName: currentAppName, onRetry: () => load(true) }) })] }))] }));
}
/**
 * Everything inside the panel below the filter box.
 *
 * ⛔ There is no branch that renders nothing. Five states, five sentences — see
 * `LauncherState` in `core.ts` for why `ready + shared:false` and `ready + no siblings` are
 * different things that the four measured implementations both rendered as a blank.
 *
 * ⭐ EXPORTED, AND EFFECT-FREE ON PURPOSE. This is where every tile, every accessible name
 * and every honest sentence is produced, so it is the half worth asserting in CI — and
 * because it holds no `useEffect` and no `useState`, `renderToStaticMarkup` renders it
 * completely. That is what lets `render.test.ts` measure the real markup with **no jsdom
 * and no `act`**: Data Studio's frontend has no test runner at all (measured 2026-09-21 —
 * no vitest, no test script, zero test files of its own), Converse has no
 * `@testing-library/react`, and `act` moved from `react-dom/test-utils` to `react` between
 * the React 18 the package is built against and the React 19 two consumers run. A test that
 * needs none of them runs in all four repositories and in the package's own gate.
 *
 * The stateful shell above owns the fetch, the open/closed state and focus; those are
 * verified live in a browser, which is where focus behaviour is real anyway.
 */
export function SuiteLauncherBody({ state, query = '', currentAppName, onRetry, }) {
    if (state.status === 'idle' || state.status === 'loading') {
        return (_jsx("p", { className: "vsl-note", role: "status", children: "Loading your applications\u2026" }));
    }
    if (state.status === 'error') {
        return (_jsxs("div", { role: "alert", className: "vsl-error", children: [_jsx("p", { className: "vsl-note", children: state.message }), _jsx("button", { type: "button", className: "vsl-retry", onClick: () => onRetry?.(), children: "Try again" })] }));
    }
    if (!state.shared) {
        // Signed in to this application directly, with no suite cookie. `suite_launcher` will
        // not answer for siblings in that case — deliberately, because answering would mean a
        // second copy of the access rules — so there is nothing to list and a reason to give.
        // ⛔ No "Continue with Vaarta" button and no link to Identity Studio: applications own
        // their own sign-in, and sending somebody to a page they did not ask for is the thing
        // that rule forbids.
        return (_jsxs("p", { className: "vsl-note", children: ["This sign-in covers ", currentAppName ? _jsx("strong", { children: currentAppName }) : 'this application', " only, so your other Vaarta applications cannot be listed here. Signing in through Vaarta lists them."] }));
    }
    const groups = groupTiles(filterTiles(state.tiles, query));
    if (!groups.length) {
        return (_jsx("p", { className: "vsl-note", children: query
                ? `No application matches “${query}”.`
                : 'You have no other Vaarta applications yet.' }));
    }
    return (_jsx(_Fragment, { children: groups.map((group) => (_jsx(Section, { label: group.label, tiles: group.tiles }, group.label ?? ' ungrouped'))) }));
}
function Section({ label, tiles }) {
    const headingId = useId();
    return (_jsxs("section", { "aria-labelledby": label ? headingId : undefined, className: "vsl-section", children: [label && (_jsx("h3", { id: headingId, className: "vsl-section-title", children: label })), _jsx("div", { className: "vsl-grid", children: tiles.map((tile, index) => (_jsx(Tile, { tile: tile }, `${tile.name}-${index}`))) })] }));
}
/**
 * One tile, in one of three shapes — and every one of them has an accessible name and is
 * reachable from the keyboard.
 *
 * ⚠️ That last part is why an unlaunchable tile is `aria-disabled` and not `disabled`: a
 * `disabled` button is removed from the tab order, so a person driving by keyboard would
 * never learn the tile is there. `aria-disabled` announces the state and keeps the stop.
 *
 * ⭐ The CURRENT application is a real link to its own origin, not an inert element. Going
 * to your own product's front door is harmless, it is what Zoho's launcher does, and the
 * alternative — a `<div>` with text in it — is an element with no role whose name a
 * screen reader assembles by accident. `aria-current="page"` carries the meaning instead.
 */
function Tile({ tile }) {
    const art = _jsx(TileArt, { tile: tile });
    // ⛔ From `core.tileAccessibleName`, not composed in JSX text: the a11y tree is what gets
    // measured, so the string is produced by a function the tests assert directly and the
    // live reading checks the same string rather than a similar one.
    const name = tileAccessibleName(tile);
    const kind = tileKind(tile);
    const body = (_jsxs(_Fragment, { children: [art, _jsx("span", { className: "vsl-tile-name", children: tile.name }), tile.current && _jsx("span", { className: "vsl-tile-note", children: "You are here" }), !tile.current && tile.mfaRequired && _jsx("span", { className: "vsl-tile-note", children: "2FA" }), !tile.current && !tile.launchUrl && _jsx("span", { className: "vsl-tile-note", children: "No address" })] }));
    if (kind === 'inert') {
        return (_jsx("button", { type: "button", className: "vsl-tile vsl-tile--inert", "aria-disabled": "true", "aria-label": name, [TILE_ATTR]: '', onClick: (event) => event.preventDefault(), children: body }));
    }
    return (_jsx("a", { className: `vsl-tile${kind === 'current' ? ' vsl-tile--current' : ''}`, href: tile.launchUrl ?? undefined, "aria-label": name, "aria-current": tile.current ? 'page' : undefined, [TILE_ATTR]: '', children: body }));
}
/**
 * The artwork. `logoUrl` when Identity Studio has one, initials when it does not.
 *
 * ⚠️ `logo_url` was NULL for every registered application when this was written, so the
 * monogram is not a fallback in practice — it is what tiles look like. `alt=""` on the
 * image and `aria-hidden` on the monogram because the tile's own `aria-label` already
 * names the application; a second reading of the name is noise.
 */
function TileArt({ tile }) {
    if (tile.logoUrl) {
        return _jsx("img", { className: "vsl-tile-art", src: tile.logoUrl, alt: "", width: 32, height: 32 });
    }
    const hue = tileAccent(tile.name);
    return (_jsx("span", { className: "vsl-tile-art vsl-tile-art--mono", "aria-hidden": "true", style: {
            // `color-mix` is avoided: Data Studio is on Vite 5 with an older browserslist and
            // these are inline styles no build step will polyfill. hsl works everywhere.
            background: `hsl(${hue} 62% 46%)`,
            color: '#fff',
        }, children: monogram(tile.name) }));
}
/** The 3×3 waffle. Inline, because `lucide-react` is at four different versions across the
 *  four consumers and one icon is not worth a fifth shared dependency. */
function WaffleIcon() {
    return (_jsx("svg", { width: "18", height: "18", viewBox: "0 0 18 18", "aria-hidden": "true", focusable: "false", children: [1, 7, 13].map((y) => [1, 7, 13].map((x) => _jsx("rect", { x: x, y: y, width: "4", height: "4", rx: "1", fill: "currentColor" }, `${x}-${y}`))) }));
}
function allTiles(panel) {
    if (!panel)
        return [];
    return Array.from(panel.querySelectorAll(`[${TILE_ATTR}]`));
}
function firstTile(panel) {
    return allTiles(panel)[0] ?? null;
}
const STYLE_ID = 'vaarta-suite-launcher-styles';
/**
 * Inject the stylesheet once per document.
 *
 * Only the rules inline styles cannot express live here: `:focus-visible`, `:hover`, the
 * phone-width `@media`, and the dark-scheme overrides. Everything is scoped under `.vsl-`
 * so mounting this inside four different design systems cannot collide with them, and the
 * custom properties are defined on `.vsl-root` so a host can override any colour without
 * this file knowing.
 */
function useStyleSheet() {
    useEffect(() => {
        if (typeof document === 'undefined')
            return;
        if (document.getElementById(STYLE_ID))
            return;
        const el = document.createElement('style');
        el.id = STYLE_ID;
        el.textContent = STYLES;
        document.head.appendChild(el);
    }, []);
}
const STYLES = `
.vsl-root{
  --vsl-bg:#fff; --vsl-fg:#18181b; --vsl-muted:#6b7280; --vsl-border:#e4e4e7;
  --vsl-hover:#f4f4f5; --vsl-ring:#6366f1; --vsl-shadow:0 12px 32px rgba(15,23,42,.18);
  --vsl-radius:12px;
}
@media (prefers-color-scheme: dark){
  .vsl-root{
    --vsl-bg:#18181b; --vsl-fg:#fafafa; --vsl-muted:#a1a1aa; --vsl-border:#3f3f46;
    --vsl-hover:#27272a; --vsl-shadow:0 12px 32px rgba(0,0,0,.55);
  }
}
.vsl-trigger{
  display:inline-flex; align-items:center; justify-content:center;
  width:34px; height:34px; padding:0; border:0; border-radius:8px;
  background:transparent; color:var(--vsl-fg); cursor:pointer; opacity:.78;
}
.vsl-trigger:hover{ background:var(--vsl-hover); opacity:1; }
.vsl-trigger:focus-visible, .vsl-tile:focus-visible, .vsl-retry:focus-visible, .vsl-filter:focus-visible{
  outline:2px solid var(--vsl-ring); outline-offset:2px;
}
.vsl-panel{
  position:absolute; z-index:1000;
  /* ⚠️ 680px, not 520: measured in Chrome with SEVEN applications in five categories —
     the size this was explicitly designed for — the content is ~660px and a 520px cap made
     the panel scroll at exactly the count it was built to hold. Five headings and five tile
     rows is simply that tall; the fix is the cap and the vertical rhythm below, not fewer
     sections. It still scrolls gracefully on a short viewport, which is what 82vh is for. */
  width:352px; max-height:min(82vh,680px); overflow:auto;
  display:flex; flex-direction:column; gap:9px; padding:14px;
  background:var(--vsl-bg); color:var(--vsl-fg);
  border:1px solid var(--vsl-border); border-radius:var(--vsl-radius); box-shadow:var(--vsl-shadow);
}
.vsl-panel--right{ right:0; } .vsl-panel--left{ left:0; }
/* Which way it opens. --down is the default a top bar wants; --up is what a trigger in a
   sidebar FOOTER needs, where opening downward puts the panel below the fold.
   NOTE: no backticks in this block -- it lives inside a template literal. */
.vsl-panel--down{ top:calc(100% + 8px); bottom:auto; }
.vsl-panel--up{ bottom:calc(100% + 8px); top:auto; }
.vsl-panel-head{ display:flex; align-items:center; }
.vsl-panel-title{ font-size:11px; font-weight:600; letter-spacing:.06em; text-transform:uppercase; color:var(--vsl-muted); }
.vsl-filter{
  width:100%; box-sizing:border-box; padding:7px 10px; font-size:13px;
  color:var(--vsl-fg); background:var(--vsl-bg);
  border:1px solid var(--vsl-border); border-radius:8px;
}
.vsl-body{ display:flex; flex-direction:column; gap:10px; }
.vsl-section{ display:flex; flex-direction:column; gap:5px; }
.vsl-section-title{ margin:0; font-size:11px; font-weight:600; letter-spacing:.05em; text-transform:uppercase; color:var(--vsl-muted); }
.vsl-grid{ display:grid; grid-template-columns:repeat(auto-fill,minmax(96px,1fr)); gap:6px; }
.vsl-tile{
  display:flex; flex-direction:column; align-items:center; gap:5px;
  padding:9px 6px; border:1px solid transparent; border-radius:10px;
  background:transparent; color:inherit; font:inherit; text-align:center;
  text-decoration:none; cursor:pointer;
}
.vsl-tile:hover{ background:var(--vsl-hover); }
.vsl-tile--current{ border-color:var(--vsl-border); background:var(--vsl-hover); }
.vsl-tile--inert{ cursor:default; opacity:.6; }
.vsl-tile--inert:hover{ background:transparent; }
.vsl-tile-art{
  display:flex; align-items:center; justify-content:center;
  width:32px; height:32px; border-radius:9px; object-fit:cover; flex:0 0 auto;
}
.vsl-tile-art--mono{ font-size:13px; font-weight:700; letter-spacing:.02em; }
.vsl-tile-name{ font-size:12px; line-height:1.3; overflow-wrap:anywhere; }
.vsl-tile-note{ font-size:10px; color:var(--vsl-muted); }
.vsl-note{ margin:0; font-size:12.5px; line-height:1.5; color:var(--vsl-muted); }
.vsl-error{ display:flex; flex-direction:column; align-items:flex-start; gap:8px; }
.vsl-retry{
  padding:5px 10px; font-size:12px; font-weight:500; color:var(--vsl-fg);
  background:var(--vsl-bg); border:1px solid var(--vsl-border); border-radius:7px; cursor:pointer;
}
.vsl-retry:hover{ background:var(--vsl-hover); }
/* Phone width: the panel stops being a dropdown anchored to a corner it may not fit beside,
   and becomes a sheet inset from both edges. 352px does not fit a 360px viewport with a
   gutter, and a panel clipped off-screen is the bug this rule exists for. */
@media (max-width:480px){
  /* The sheet overrides BOTH drop directions — at this width the panel is pinned to the
     bottom of the viewport regardless of where its trigger sits. */
  .vsl-panel, .vsl-panel--up, .vsl-panel--down{
    position:fixed; top:auto; bottom:12px; left:12px; right:12px;
    width:auto; max-height:72vh;
  }
  .vsl-grid{ grid-template-columns:repeat(auto-fill,minmax(88px,1fr)); }
}
@media (prefers-reduced-motion:no-preference){
  .vsl-panel{ animation:vsl-in .12s ease-out; }
  @keyframes vsl-in{ from{ opacity:0; transform:translateY(-4px); } to{ opacity:1; transform:none; } }
}
`;
